import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './store';
import {
  ChatType,
  HeroConversation,
  HeroConversationMessage,
  Product,
  SavedReplyType,
  ShopperEventCategory,
  UploadedFile,
} from '../model';

type ConversationState = {
  localMessages: HeroConversationMessage[];
  uploadedFiles: UploadedFile[];
  sharedProducts: Product[];
};

interface ChatState {
  selectedSavedReplyType: SavedReplyType;
  selectedChatType: 'active' | 'archived';
  selectedConversation?: HeroConversation;
  selectedSavedReplyContent?: string;
  savedRepliesModalOpen: boolean;
  conversations: Record<string, ConversationState>;
  shopperProfile: {
    selectedShopperEventCategory?: ShopperEventCategory;
  };
}

const initialState: ChatState = {
  selectedChatType: 'active',
  conversations: {},
  savedRepliesModalOpen: false,
  selectedSavedReplyType: 'all',
  shopperProfile: {
    selectedShopperEventCategory: undefined,
  },
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setSelectedShopperEventCategory: (
      state,
      action: PayloadAction<ShopperEventCategory | undefined>,
    ) => {
      state.shopperProfile.selectedShopperEventCategory = action.payload;
    },
    removeConversationSharedProduct: (
      state,
      action: PayloadAction<{ conversationId: string; product: Product }>,
    ) => {
      const { conversationId, product } = action.payload;
      state.conversations[conversationId].sharedProducts = state.conversations[
        conversationId
      ].sharedProducts.filter(
        (currentProduct) => currentProduct.sku !== product.sku,
      );
    },
    addConversationSharedProduct: (
      state,
      action: PayloadAction<{ conversationId: string; product: Product }>,
    ) => {
      const { conversationId, product } = action.payload;
      state.conversations[conversationId].sharedProducts = [
        ...state.conversations[conversationId].sharedProducts,
        product,
      ];
    },
    setSelectedSavedReplyType: (
      state,
      action: PayloadAction<SavedReplyType>,
    ) => {
      state.selectedSavedReplyType = action.payload;
    },
    setSelectedSavedReplyContent: (state, action: PayloadAction<string>) => {
      state.selectedSavedReplyContent = action.payload;
    },
    clearSelectedSavedReplyContent: (state) => {
      state.selectedSavedReplyContent = undefined;
    },
    setSelectedChatType: (state, action: PayloadAction<ChatType>) => {
      state.selectedChatType = action.payload;
    },
    setSelectedConversation: (
      state,
      action: PayloadAction<HeroConversation | undefined>,
    ) => {
      const selectedConversation = action.payload;
      state.selectedConversation = selectedConversation;
      if (selectedConversation) {
        state.conversations[selectedConversation.id] = state.conversations[
          selectedConversation.id
        ] || {
          localMessages: [],
          uploadedFiles: [],
          sharedProducts: [],
        };
      }
    },
    clearConversationUploadedFiles: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      state.conversations[conversationId].uploadedFiles = [];
    },
    clearConversationSharedProducts: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      state.conversations[conversationId].sharedProducts = [];
    },
    removeConversationUploadedFile: (
      state,
      action: PayloadAction<{ conversationId: string; fileUrl: string }>,
    ) => {
      const { conversationId, fileUrl } = action.payload;
      state.conversations[conversationId].uploadedFiles = state.conversations[
        conversationId
      ].uploadedFiles.filter((file) => file.url !== fileUrl);
    },
    addConversationUploadedFiles: (
      state,
      action: PayloadAction<{ conversationId: string; files: UploadedFile[] }>,
    ) => {
      const { conversationId, files } = action.payload;
      state.conversations[conversationId].uploadedFiles = [
        ...state.conversations[conversationId].uploadedFiles,
        ...files,
      ];
    },
    setSavedRepliesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.savedRepliesModalOpen = action.payload;
    },
    addLocalMessage: (
      state,
      action: PayloadAction<{
        conversationId: string;
        localMessage: HeroConversationMessage;
      }>,
    ) => {
      const { conversationId, localMessage } = action.payload;
      // @ts-expect-error - TS doesn't know that the conversationId key exists
      state.conversations[conversationId].localMessages.push(localMessage);
    },
    updateLocalMessageIndex: (
      state,
      action: PayloadAction<{ localMessage: HeroConversationMessage }>,
    ) => {
      const localMessage = action.payload.localMessage;
      const conversationId = localMessage.attributes.heroConversationId;
      const localMessageIndex = state.conversations[
        conversationId
      ].localMessages.findIndex(
        (message) =>
          message.attributes.localMessageId ===
          localMessage.attributes.localMessageId,
      );
      const messageFromState =
        state.conversations[conversationId].localMessages[localMessageIndex];
      if (!messageFromState) return;
      messageFromState.index = localMessage.index;
    },
  },
});

export const selectSelectedShopperEventCategory = (state: RootState) =>
  state.chat.shopperProfile.selectedShopperEventCategory;

export const selectSelectedChatType = (state: RootState) =>
  state.chat.selectedChatType;

export const selectSelectedConversation = (state: RootState) =>
  state.chat.selectedConversation;

export const selectUploadedFilesByConversationId = (
  state: RootState,
  conversationId: string,
) => state.chat.conversations?.[conversationId]?.uploadedFiles || [];

export const selectLocalMessagesByConversationId = (
  state: RootState,
  conversationId: string,
) =>
  conversationId
    ? state.chat.conversations?.[conversationId]?.localMessages
    : [];

export const selectSavedRepliesModalOpen = (state: RootState) =>
  state.chat.savedRepliesModalOpen;

export const selectSelectedSavedReplyContent = (state: RootState) =>
  state.chat.selectedSavedReplyContent;

export const selectSelectedSavedReplyType = (state: RootState) =>
  state.chat.selectedSavedReplyType;

export const selectConversationSharedProducts = (
  state: RootState,
  conversationId: string,
) => state.chat.conversations[conversationId].sharedProducts || [];

export default chatSlice.reducer;
